import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from './AuthProvider';
import { fetchSubscriptionStatus } from '@/api';
import { safeReturnTo, splashPath, authPath } from './returnTo';

interface AuthGuardProps {
    children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
    const { isAuthenticated, isLoading, error, retryAuth } = useAuthContext();
    const { getToken, isSignedIn, isLoaded } = useAuth();
    const location = useLocation();
    const requestedLocation = safeReturnTo(`${location.pathname}${location.search}${location.hash}`);

    const [subscriptionChecked, setSubscriptionChecked] = useState(false);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [subscriptionLoading, setSubscriptionLoading] = useState(true);

    // Check subscription status when authenticated
    useEffect(() => {
        const checkSubscription = async () => {
            if (!isAuthenticated || isLoading) {
                setSubscriptionLoading(false);
                return;
            }

            try {
                const token = await getToken({ template: "Intelligenic" });
                if (!token) {
                    setHasSubscription(false);
                    setSubscriptionChecked(true);
                    setSubscriptionLoading(false);
                    return;
                }

                const status = await fetchSubscriptionStatus(token);
                setHasSubscription(status.has_subscription);
            } catch (err) {
                console.error('AuthGuard: Failed to check subscription status:', err);
                setHasSubscription(false);
            } finally {
                setSubscriptionChecked(true);
                setSubscriptionLoading(false);
            }
        };

        checkSubscription();
    }, [isAuthenticated, isLoading, getToken]);

    // Show loading while authentication or subscription check is in progress
    if (isLoading || subscriptionLoading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    flexDirection: 'column',
                    fontFamily: 'Arial, sans-serif'
                }}
            >
                <div style={{ marginBottom: '20px' }}>
                    Loading...
                </div>
                <div
                    style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #3498db',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}
                />
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // If there's an error but user is signed in with Clerk, redirect to /auth to complete auth flow
    // This handles cases where user lands on /dashboard directly from external redirect (e.g., plans page)
    if (error && isLoaded && isSignedIn) {
        console.log('AuthGuard: Error loading profile but user is signed in, redirecting to /auth');
        return <Navigate to={authPath(requestedLocation)} replace />;
    }

    // Show error state with retry option (for cases where user is not signed in)
    if (error) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    flexDirection: 'column',
                    fontFamily: 'Arial, sans-serif',
                    padding: '20px'
                }}
            >
                <div style={{ color: 'red', marginBottom: '20px', textAlign: 'center' }}>
                    Authentication Error: {error}
                </div>
                <button
                    onClick={retryAuth}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#3498db',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginBottom: '10px'
                    }}
                >
                    Retry
                </button>
                <button
                    onClick={() => window.location.href = '/splash'}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#95a5a6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Back to Login
                </button>
            </div>
        );
    }

    // If not authenticated, redirect to splash page
    if (!isAuthenticated) {
        console.log('AuthGuard: Not authenticated, redirecting to splash');
        return <Navigate to={splashPath(requestedLocation)} replace />;
    }

    // If authenticated but no subscription, redirect to external plans page
    if (subscriptionChecked && !hasSubscription) {
        const plansUrl = import.meta.env.VITE_PLANS_PAGE_URL || 'https://webstage.intelligenic.ai/plans/';
        console.log('AuthGuard: No active subscription, redirecting to plans page');
        window.location.href = plansUrl;
        return null;
    }

    // User is authenticated and has subscription, render the protected content
    return <>{children}</>;
};

export default AuthGuard;
